
import { createDrawingCanvas } from './canvas-utils';

// Helper function to format a title by replacing underscores and capitalizing
export const formatTitle = (title: string): string => {
  return title
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Draw text with wrapping - enhanced for high resolution
export const drawWrappedText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  startX: number,
  startY: number,
  lineHeight: number
): number => {
  const words = text.split(' ');
  let line = '';
  let currentY = startY;
  
  // Fix text alignment issue
  const textAlign = ctx.textAlign;
  ctx.textAlign = 'left';
  
  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;
    
    if (testWidth > maxWidth && n > 0) {
      // Apply text alignment only for rendering
      const xPos = textAlign === 'center' ? startX + (maxWidth / 2) : startX;
      ctx.fillText(line, xPos, currentY);
      line = words[n] + ' ';
      currentY += lineHeight;
      
      if (currentY > 3540) { // Adjusted for higher resolution
        ctx.fillText('...', xPos, currentY);
        return currentY;
      }
    } else {
      line = testLine;
    }
  }
  
  if (currentY <= 3540) { // Adjusted for higher resolution
    const xPos = textAlign === 'center' ? startX + (maxWidth / 2) : startX;
    ctx.fillText(line, xPos, currentY);
  }
  
  // Restore original text alignment
  ctx.textAlign = textAlign;
  
  return currentY + lineHeight;
};

// Base URLs for assets
export const SUPABASE_STORAGE_URL = "https://tmmhqtkkmoivcgjolyjp.supabase.co/storage/v1/object/public";
export const ASSETS_BUCKET = "assets";
export const LOGO_PATH = `${SUPABASE_STORAGE_URL}/${ASSETS_BUCKET}/logo.jpg`;
export const FALLBACK_LOGO_PATH = '/assets/logo.jpg';

// Logo cache utilities
const LOGO_CACHE_KEY = 'app_logo_cache';
const LOGO_CACHE_TIMESTAMP_KEY = 'app_logo_cache_timestamp';
const LOGO_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

interface LogoCache {
  dataUrl: string;
  timestamp: number;
}

// Utility to check if an image exists
export const imageExists = (url: string): Promise<boolean> => {
  console.log('Checking if image exists at:', url);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      console.log('Image found at:', url);
      resolve(true);
    };
    img.onerror = () => {
      console.warn('Image not found at:', url);
      resolve(false);
    };
    img.src = url;
  });
};

// Convert image URL to data URL to avoid CORS issues
export const getImageAsDataUrl = async (imageUrl: string): Promise<string | null> => {
  try {
    console.log('Fetching image as data URL from:', imageUrl);
    const response = await fetch(imageUrl, { mode: 'cors', cache: 'no-cache' });
    
    if (!response.ok) {
      console.error('Failed to fetch image:', response.status, response.statusText);
      return null;
    }
    
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => {
        console.error('Error converting image to data URL');
        resolve(null);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error fetching image as data URL:', error);
    return null;
  }
};

// Get cached logo if available and not expired
export const getCachedLogo = (): string | null => {
  try {
    const cachedData = localStorage.getItem(LOGO_CACHE_KEY);
    if (!cachedData) return null;
    
    const cachedLogo = JSON.parse(cachedData) as LogoCache;
    const now = Date.now();
    
    if (now - cachedLogo.timestamp > LOGO_CACHE_DURATION) {
      console.log('Logo cache expired, clearing cache');
      localStorage.removeItem(LOGO_CACHE_KEY);
      localStorage.removeItem(LOGO_CACHE_TIMESTAMP_KEY);
      return null;
    }
    
    console.log('Using cached logo data URL');
    return cachedLogo.dataUrl;
  } catch (error) {
    console.error('Error reading from logo cache:', error);
    return null;
  }
};

// Cache logo data URL with timestamp
export const cacheLogo = (dataUrl: string): void => {
  try {
    const timestamp = Date.now();
    const cacheData: LogoCache = { dataUrl, timestamp };
    
    localStorage.setItem(LOGO_CACHE_KEY, JSON.stringify(cacheData));
    localStorage.setItem(LOGO_CACHE_TIMESTAMP_KEY, timestamp.toString());
    console.log('Logo cached successfully');
  } catch (error) {
    console.error('Error caching logo:', error);
  }
};

// Clear logo cache
export const clearLogoCache = (): void => {
  try {
    localStorage.removeItem(LOGO_CACHE_KEY);
    localStorage.removeItem(LOGO_CACHE_TIMESTAMP_KEY);
    console.log('Logo cache cleared successfully');
  } catch (error) {
    console.error('Error clearing logo cache:', error);
  }
};

// Preload logo and get it as dataURL before starting the export process
export const preloadLogo = async (): Promise<string | null> => {
  console.log('Preloading logo for export...');
  
  let logoDataUrl = getCachedLogo();
  
  if (logoDataUrl) {
    console.log('Using preloaded logo from cache');
    return logoDataUrl;
  }
  
  console.log('No cached logo found, checking Supabase...');
  const supabaseLogoExists = await imageExists(LOGO_PATH);
  
  if (supabaseLogoExists) {
    console.log('Logo exists in Supabase, converting to data URL');
    logoDataUrl = await getImageAsDataUrl(LOGO_PATH);
    
    if (logoDataUrl) {
      cacheLogo(logoDataUrl);
      console.log('Supabase logo preloaded and cached successfully');
      return logoDataUrl;
    }
  }
  
  console.log('Supabase logo failed or not found, trying local fallback');
  const localLogoExists = await imageExists(FALLBACK_LOGO_PATH);
  
  if (localLogoExists) {
    logoDataUrl = await getImageAsDataUrl(FALLBACK_LOGO_PATH);
    
    if (logoDataUrl) {
      cacheLogo(logoDataUrl);
      console.log('Fallback logo preloaded and cached successfully');
      return logoDataUrl;
    }
  }
  
  console.warn('Failed to preload any logo');
  return null;
};

// Function to load and draw the logo on the canvas with minimal styling - enhanced for high resolution
export const loadAndDrawLogo = (
  ctx: CanvasRenderingContext2D,
  logoDataUrl: string,
  canvasWidth: number,
  headerHeight: number,
  logoSize = 72 // Doubled default parameter for logo size
): Promise<void> => {
  return new Promise((resolve) => {
    console.log('Drawing logo in header');
    
    const logoImg = new Image();
    
    logoImg.onload = () => {
      try {
        const logoX = canvasWidth - logoSize - 60;
        const logoY = (headerHeight - logoSize) / 2;
        
        // Draw circular avatar with high-quality anti-aliasing
        ctx.save();
        ctx.beginPath();
        ctx.arc(logoX + logoSize/2, logoY + logoSize/2, logoSize/2, 0, Math.PI * 2);
        ctx.clip();
        
        // High quality image rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
        ctx.restore();
        
        console.log('Logo successfully drawn in header');
        resolve();
      } catch (error) {
        console.error('Error drawing logo:', error);
        resolve();
      }
    };
    
    logoImg.onerror = (err) => {
      console.error('Error loading logo from data URL:', err);
      resolve();
    };
    
    logoImg.src = logoDataUrl;
  });
};
